import { useCallback, useEffect, useRef, useState } from 'react';

import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type {
	AiFeatureRequestEnvelopeConfluenceHeaderImageGenerationRequest,
	ConfluenceHeaderImageGenerationResponse,
} from '@atlassian/convo-ai-api';

import type {
	AIHeaderImageStatus,
	GenerateImageProps,
	UseAIGeneratedCoverImageProps,
	UseAIGeneratedCoverImageReturn,
} from '../types';

const CONVO_AI_URL = '/gateway/api/assist/api/ai/v2/ai-feature/confluence-header-image-generation';

export const useAIGeneratedCoverImage = ({
	contentId,
	eventCallbacks,
}: UseAIGeneratedCoverImageProps): UseAIGeneratedCoverImageReturn => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [data, setData] = useState<ConfluenceHeaderImageGenerationResponse | null>(null);
	const [aiHeaderImageStatus, setAIHeaderImageStatus] =
		useState<AIHeaderImageStatus>('NOT_LOADING');
	const showAcceptanceFlowRef = useRef(false);
	const abortControllerRef = useRef<AbortController | null>(null);

	useEffect(() => {
		if (error) {
			setAIHeaderImageStatus('ERROR');
			return;
		}

		if (loading) {
			if (showAcceptanceFlowRef.current) {
				setAIHeaderImageStatus('LOADING_WITH_ACCEPTANCE_FLOW');
			} else {
				setAIHeaderImageStatus('LOADING');
			}
		} else {
			setAIHeaderImageStatus('NOT_LOADING');
		}
	}, [loading, error]);

	const generateImage = useCallback(
		async ({
			adfContent,
			aspectRatio,
			showAcceptanceFlow = false,
			useRawPrompt = false,
			theme,
		}: GenerateImageProps): Promise<string | undefined> => {
			const eventParams = {
				contentId,
				useRawPrompt: useRawPrompt || false,
				...(theme && { theme }),
				...(aspectRatio && { aspectRatio }),
			};

			// Notify start
			eventCallbacks?.onGenerateStart?.(eventParams);

			// Abort potential ongoing request
			abortControllerRef.current?.abort();
			abortControllerRef.current = new AbortController();

			showAcceptanceFlowRef.current = showAcceptanceFlow;
			setError(null);
			setData(null);

			if (!loading) {
				setLoading(true);
			}

			try {
				const useAdvancedImageGeneration = expValEquals(
					'confluence_advanced_header_image_generation_exp',
					'isEnabled',
					true,
				);
				const request: AiFeatureRequestEnvelopeConfluenceHeaderImageGenerationRequest = {
					ai_feature_input: {
						adfContent,
						contentId,
						useRawPrompt,
						...(useAdvancedImageGeneration && { useAdvancedImageGeneration }),
						...(theme !== undefined && { theme }),
						...(aspectRatio !== undefined && { aspectRatio }),
					},
				};

				const response = await fetch(CONVO_AI_URL, {
					method: 'POST',
					headers: {
						'content-type': 'application/json;charset=UTF-8',
						'X-Experience-Id': 'confluence-ai-first-creation',
						'X-Product': 'confluence',
					},
					body: JSON.stringify(request),
					signal: abortControllerRef.current.signal,
				});

				if (!response.ok) {
					throw new Error(
						`Error fetching AI-generated cover image, status code: ${response.status}`,
					);
				}

				const responseData = await response.json();
				const content = responseData?.ai_feature_output;

				if (!content) {
					const noContentError = new Error('No content received from API');
					setError(noContentError.message);
					eventCallbacks?.onGenerateError?.({ ...eventParams, error: noContentError });
					return;
				}

				// Check if the response contains an error in the body (API can return 200 OK with error in body)
				if (content.error) {
					const errorMessage =
						content.error.error_message ||
						content.error.error_reason ||
						'AI image generation failed';
					const apiError = new Error(errorMessage);
					setError(errorMessage);
					eventCallbacks?.onGenerateError?.({ ...eventParams, error: apiError });
					return;
				}

				const responseContent = content as ConfluenceHeaderImageGenerationResponse;
				setData(responseContent);

				// Notify success
				eventCallbacks?.onGenerateSuccess?.(eventParams);

				return responseContent.mediaFileId;
			} catch (err) {
				const isErrorType = err instanceof Error;

				// Don't set error for aborted requests
				if (isErrorType && err.name === 'AbortError') {
					eventCallbacks?.onGenerateAbort?.(eventParams);
					return;
				}

				const errorMessage = isErrorType ? err.message : 'Unknown error occurred';
				const error = isErrorType ? err : new Error(errorMessage);
				setError(errorMessage);

				eventCallbacks?.onGenerateError?.({ ...eventParams, error });
			} finally {
				setLoading(false);
			}
		},
		[contentId, loading, eventCallbacks],
	);

	// Begin loading experience but don't generate yet
	const fakeGenerateImage = useCallback(() => {
		showAcceptanceFlowRef.current = false;
		setError(null);
		setData(null);
		setLoading(true);
	}, []);

	return {
		generateImage,
		fakeGenerateImage,
		loading,
		error,
		data,
		aiHeaderImageStatus,
		aiHeaderImageFileId: data?.mediaFileId,
		setAIHeaderImageStatus,
	};
};
